import { useState } from 'react'
import { Stack } from '@common/ui'
import { IconLogin2 } from '@common/ui/icons'
import { com } from '@eidu/web'
import { PermissionWithId } from '../input/PermissionsInput'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import actionLabel from '../../domain/permission/actionLabel'
import GlobalSection from './GlobalSection'
import EntitiesSection from './EntitiesSection'
import CellEditor from './CellEditor'
import UnassignedPermissionsList from './UnassignedPermissionsList'
import {
  ColDef,
  EntityRow,
  GLOBAL_ENTITY_ACTIONS,
  GlobalRow,
  anchorTypeIdOf,
  buildGlobalPermission,
  deriveUserEntityTypeId,
  isAllRowPermission,
  isAnchorable,
  isGlobalRowPermission,
  isUserSelectorAnchored,
} from './utils'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import EntitySelector = com.eidu.entity.selector.EntitySelector
import GlobalAction = com.eidu.entity.permission.GlobalAction
import GlobalPermission = com.eidu.entity.permission.GlobalPermission
import EntityAction = com.eidu.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import Permission = com.eidu.entity.permission.Permission

type PermissionsMatrixProps = {
  entityTypes: readonly EntityType[]
  userSelector: EntitySelector
  selectedPermissions: readonly PermissionWithId[]
  setSelectedPermissions: (permissions: readonly PermissionWithId[]) => void
  selectedPolicies?: ReadonlyArray<PermissionPolicyDTO>
}

const PermissionsMatrix = ({
  entityTypes,
  userSelector,
  selectedPermissions,
  setSelectedPermissions,
  selectedPolicies,
}: PermissionsMatrixProps) => {
  const [expandedCell, setExpandedCell] = useState<string | null>(null)
  const [expandedGlobalRowId, setExpandedGlobalRowId] = useState<string | null>(null)
  const [globalSectionOpen, setGlobalSectionOpen] = useState(true)
  const [entitiesSectionOpen, setEntitiesSectionOpen] = useState(true)

  const closeExpansion = () => setExpandedCell(null)

  const globalRows: GlobalRow[] = [
    ...GlobalAction.values().map<GlobalRow>((action) => ({
      id: `global-${action}`,
      action,
      label: actionLabel(action),
    })),
    ...GLOBAL_ENTITY_ACTIONS.map<GlobalRow>((action) => ({
      id: `global-${action}`,
      action,
      label: actionLabel(action),
    })),
  ]

  const entityRows: EntityRow[] = [...entityTypes]
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
    .map((type) => ({
      id: `type-${type.id.toString()}`,
      entityTypeId: type.id,
      entityType: type,
      label: String(type.name),
    }))

  const cellPermissions = (row: EntityRow, col: ColDef): PermissionWithId[] =>
    selectedPermissions.filter(({ permission }) => {
      if (permission.action !== col.action) return false
      const anchor = anchorTypeIdOf(permission, entityTypes)
      return anchor !== null && anchor.equals(row.entityTypeId)
    })

  const replacePermission = (id: string, newPermission: Permission) =>
    setSelectedPermissions(selectedPermissions.map((p) => (p.id === id ? { id, permission: newPermission } : p)))

  const removePermission = (id: string) => setSelectedPermissions(selectedPermissions.filter((p) => p.id !== id))

  const addPermission = (permission: Permission) =>
    setSelectedPermissions([...selectedPermissions, { id: crypto.randomUUID(), permission }])

  const toggleGlobalRow = (row: GlobalRow) => {
    closeExpansion()
    const existing = selectedPermissions.find((p) => isGlobalRowPermission(p.permission, row.action))
    if (existing) removePermission(existing.id)
    else addPermission(buildGlobalPermission(row.action))
  }

  const toggleEntityCell = (row: EntityRow, col: ColDef) => {
    closeExpansion()
    if (hasColumnAll(col)) narrowAllToOtherRows(row, col)
    else if (cellPermissions(row, col).length > 0) removeRowPermissions(row, col)
    else grantRow(row, col)
  }

  const grantRow = (row: EntityRow, col: ColDef) =>
    addPermission(new EntitySelectorPermission(col.action, new EntitySelector.OfType(row.entityTypeId)))

  const removeRowPermissions = (row: EntityRow, col: ColDef) => {
    const idsToRemove = new Set(cellPermissions(row, col).map((p) => p.id))
    setSelectedPermissions(selectedPermissions.filter((p) => !idsToRemove.has(p.id)))
  }

  const narrowAllToOtherRows = (row: EntityRow, col: ColDef) => {
    const withoutAll = selectedPermissions.filter(({ permission }) => !isAllRowPermission(permission, col.action))
    const fillIns: PermissionWithId[] = entityRows
      .filter((r) => !r.entityTypeId.equals(row.entityTypeId))
      // A form-only column narrows only across the rows it renders, so it never grants on other kinds.
      .filter((r) => !col.appliesToRow || col.appliesToRow(r))
      .map((r) => ({
        id: crypto.randomUUID(),
        permission: new EntitySelectorPermission(col.action, new EntitySelector.OfType(r.entityTypeId)),
      }))
    setSelectedPermissions([...withoutAll, ...fillIns])
  }

  const hasColumnAll = (col: ColDef) =>
    selectedPermissions.some(({ permission }) => isAllRowPermission(permission, col.action))

  const toggleColumn = (col: ColDef) => {
    closeExpansion()
    if (hasColumnAll(col)) {
      setSelectedPermissions(selectedPermissions.filter(({ permission }) => permission.action !== col.action))
    } else {
      addPermission(new EntitySelectorPermission(col.action, EntitySelector.All))
    }
  }

  const toggleExpand = (key: string) => {
    setExpandedCell((prev) => (prev === key ? null : key))
  }

  const unassignedPermissions = selectedPermissions.filter(({ permission }) => {
    if (permission instanceof GlobalPermission) return false
    if (permission instanceof EntitySelectorPermission && EntitySelector.All.equals(permission.targets)) {
      const action = permission.action
      if (action instanceof EntityAction) return false
    }
    return !isAnchorable(permission)
  })

  const userEntityTypeId = deriveUserEntityTypeId(userSelector, entityTypes)
  const allowsPathForUser = isUserSelectorAnchored(userSelector)

  // ShareSchoolLogin stays in the Global section but supports Assignment (path) grants. It targets the School
  // entity type, so we reuse the entity matrix's cell editor scoped to that type. The row's checkbox keeps
  // toggling the All grant; the expandable editor authors scoped/path grants.
  const schoolType = entityTypes.find((t) => t.kind.equals(EntityTypeKind.Companion.School))
  const shareSchoolLoginCol: ColDef = {
    action: EntityAction.ShareSchoolLogin,
    label: 'Share school logins',
    Icon: IconLogin2,
    allowsPath: true,
  }
  const schoolRow: EntityRow | undefined = schoolType && {
    id: `global-${EntityAction.ShareSchoolLogin}-editor`,
    entityTypeId: schoolType.id,
    entityType: schoolType,
    label: String(schoolType.name),
  }

  const renderGlobalRowEditor = (row: GlobalRow) => {
    if (row.action !== EntityAction.ShareSchoolLogin || !schoolRow) return null
    return (
      <CellEditor
        row={schoolRow}
        col={shareSchoolLoginCol}
        permissions={cellPermissions(schoolRow, shareSchoolLoginCol)}
        entityTypes={entityTypes}
        allowsPath={shareSchoolLoginCol.allowsPath && allowsPathForUser}
        userEntityTypeId={userEntityTypeId}
        onAdd={() =>
          addPermission(
            new EntitySelectorPermission(shareSchoolLoginCol.action, new EntitySelector.OfType(schoolRow.entityTypeId))
          )
        }
        onReplace={replacePermission}
        onDelete={removePermission}
      />
    )
  }

  return (
    <Stack gap="lg" style={{ width: '100%', overflowX: 'auto' }}>
      <GlobalSection
        rows={globalRows}
        open={globalSectionOpen}
        onToggleOpen={() => setGlobalSectionOpen((v) => !v)}
        isChecked={(row) => selectedPermissions.some((p) => isGlobalRowPermission(p.permission, row.action))}
        isIndeterminate={(row) =>
          selectedPermissions.some(
            (p) => p.permission.action === row.action && !isGlobalRowPermission(p.permission, row.action)
          )
        }
        onToggleRow={toggleGlobalRow}
        selectedPolicies={selectedPolicies ?? []}
        expandedRowId={expandedGlobalRowId}
        onToggleRowExpand={(id) => setExpandedGlobalRowId((prev) => (prev === id ? null : id))}
        renderRowEditor={renderGlobalRowEditor}
      />

      <EntitiesSection
        rows={entityRows}
        open={entitiesSectionOpen}
        onToggleOpen={() => setEntitiesSectionOpen((v) => !v)}
        cellPermissions={cellPermissions}
        onToggleCell={toggleEntityCell}
        onToggleColumn={toggleColumn}
        hasColumnAll={hasColumnAll}
        expandedCell={expandedCell}
        onToggleCellExpand={toggleExpand}
        onCloseExpansion={closeExpansion}
        entityTypes={entityTypes}
        allowsPathForUser={allowsPathForUser}
        userEntityTypeId={userEntityTypeId}
        addPermission={addPermission}
        replacePermission={replacePermission}
        removePermission={removePermission}
        selectedPolicies={selectedPolicies ?? []}
      />

      <UnassignedPermissionsList permissions={unassignedPermissions} onRemove={removePermission} />
    </Stack>
  )
}

export default PermissionsMatrix
